import { GraphQLError, IAPIError, ErrorSeverity } from 'types/error'
import { Nullable } from 'types/global'
import { GraphQLResponseWithoutData, PayloadError } from 'relay-runtime'

// this type needs unknown too because that's the type of the error object in the catch block
export type CaughtError = Error | IAPIError | GraphQLError | PayloadError | unknown

export function isGraphQLError(error: unknown) : error is GraphQLError {
  if (!error) return false
  if (typeof error !== 'object') return false

  if ('locations' in error) return true
  if ('path' in error) return true
  if ('extensions' in error) return true

  return false
}

export function isPayloadError(error: unknown) : error is PayloadError {
  if (!error) return false
  if (typeof error !== 'object') return false

  if ('message' in error && !('name' in error)) return true
  return false
}

export function isAPIError(error: unknown) : error is IAPIError {
  if (!error) return false
  if (typeof error !== 'object') return false

  if ('response' in error) return true
  if ('code' in error) return true

  return false
}

export function isError(error: unknown): error is Error | IAPIError | GraphQLError {
  return error instanceof Error || isAPIError(error) || isGraphQLError(error)
}

export function asError(error: unknown): Error | IAPIError | GraphQLError {
  if (isError(error)) return error

  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return new Error(error.message)
  }

  if (typeof error === 'string') {
    return new Error(error)
  }

  return new Error('Unknown Error')
}

export function isMinifiedReactError(error: unknown): boolean {
  // TODO: maybe this is not the best way to check this, but React does not use specific error types
  return Boolean(error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' && error.message?.includes('Minified React error'))
}

export function getErrorName(error: CaughtError) {
  if (isGraphQLError(error)) return error.extensions?.name
  if (isPayloadError(error)) return 'PayloadError'
  if (isAPIError(error) || error instanceof Error) return error.name
  return 'Error'
}

export function getErrorTitle(error: CaughtError): Nullable<string> {
  if (!error) return null
  const defaultMessage = 'Something went wrong'
  const errorName = getErrorName(error)
  if (errorName === 'BillingPlanIncompatibleError') return 'Upgrade your Plan to access this feature'
  if (errorName === 'BillingSetupRequiredError') return 'Set up your account first'
  return defaultMessage
}

export function getErrorCode(error: CaughtError): Nullable<number> {
  if (isGraphQLError(error) && typeof error.extensions?.statusCode === 'number') {
    return error.extensions.statusCode
  }

  if (isAPIError(error) && typeof error.code === 'number') {
    return error.code
  }

  return null
}

/**
 * Cleans a Relay error message, keeping only the important part
 * */
function cleanErrorMessage(message: string): string {
  const relayErrorRegexp = /^No data returned for operation `(?:[\w_]+)`, got error\(s\):(.+)See the error `source` property for more information\.$/
  const match = message.replaceAll('\n', '').match(relayErrorRegexp)
  if (match && match[1]) return match[1].trim()

  return message
}

export function getErrorDescription(error: CaughtError): Nullable<string> {
  if (!error) return null
  const errorName = getErrorName(error)

  if (errorName === 'BillingPlanIncompatibleError') return 'This feature is not available in your current billing plan. Use the button below to check upgrade options.'
  if (errorName === 'BillingSetupRequiredError') return 'Before continuing, you must set up a billing plan'
  if (errorName === 'BillingOverdueInvoicesError') return 'Before continuing, you must pay overdue invoices'
  if (isGraphQLError(error)) return error.message || error?.extensions?.message
  if (isAPIError(error)) return error.response || error.message
  if (isMinifiedReactError(error)) return 'Something went wrong. Try reloading the page.'

  if (error instanceof Error) {
    return cleanErrorMessage(error.message) || 'Something went wrong'
  }

  return 'Something went wrong'
}

export function getErrorText(error: CaughtError): string | null {
  // Use this if you want to display a unified error text, instead of separated title and description
  return getErrorDescription(error) || getErrorTitle(error) || null
}

/**
 * This method is useful to normalize / strip out unwanted properties from an error, for example to put it in redux
 *
 * @deprecated this should not be used anymore
 */
export function errorToObject(error: CaughtError): IAPIError | null {
  if (!error) return null

  let name

  if (isGraphQLError(error)) {
    name = name || error.extensions?.name || 'GraphQL::ExecutionError'
  } else {
    name = getErrorName(error) || 'Error'
  }

  return {
    name: name || 'Error',
    message: getErrorDescription(error) || 'Unknown Error',
    response: (error as IAPIError).response,
    code: getErrorCode(error) || undefined,
  }
}

export function objectToError(error: Nullable<IAPIError | GraphQLError | PayloadError | Error>) : Error | null {
  if (!error) return null
  if (error instanceof Error) return error

  const constructedError = new Error(error.message)
  constructedError.name = getErrorName(error) || 'Error'

  return constructedError
}

export function getErrorSeverity(error: GraphQLError | Error): Nullable<ErrorSeverity> {
  if (isGraphQLError(error) && error?.extensions?.severity) {
    return error.extensions.severity
  }

  return null
}

export function getErrorExtraInfo(error: IAPIError | GraphQLError | Error) {
  if (isGraphQLError(error)) {
    return {
      code: error?.extensions?.statusCode,
      extensions: error?.extensions,
      locations: error?.locations,
    }
  }

  if (isAPIError(error)) {
    return {
      code: error?.code,
      response: error?.response,
    }
  }

  return {}
}

export function buildGraphQLResponseWithErrorMessage(errorMessage: string): GraphQLResponseWithoutData {
  return {
    errors: [
      {
        message: errorMessage,
      },
    ],
  }
}

export function buildGraphQLResponseWithError(error: Error): GraphQLResponseWithoutData {
  return buildGraphQLResponseWithErrorMessage(error?.message)
}

/**
 * Exhaustive check utility for switch statements etc. Call this from code that is supposed to
 * be unreachable due to exhausting all reachable cases. Typescript will mark the call as an error
 * if it is actually reachable.
 *
 * @param _x the value to check for exhaustiveness
 */
export function assertUnreachable(_x: never): never {
  throw new Error('Unreachable code reached!')
}
